import type { ExecutionUnitOutput } from "@magic-md/core";
import {
  applyHydratedUnitOutputToExecutionMaps,
  type ApplyHydratedUnitOptions,
  type ExecutionMapSet,
} from "./execution-entry-builders";
import type { ExecutionEntry } from "./execution-state";

const emptyExecutionEntry: ExecutionEntry = {
  unitId: "",
  status: "idle",
  actionName: null,
  result: null,
  output: null,
  startedAt: null,
  duration: null,
};

export function cloneExecutionMaps(source: ExecutionMapSet): ExecutionMapSet {
  return {
    executions: new Map(source.executions),
    forExecutions: new Map(source.forExecutions),
    contentExecutions: new Map(source.contentExecutions),
    importExecutions: new Map(source.importExecutions),
    useExecutions: new Map(source.useExecutions),
  };
}

function reuseMapIfEntriesUnchanged<K, V>(
  source: Map<K, V>,
  next: Map<K, V>,
): Map<K, V> {
  if (source.size !== next.size) return next;
  for (const [key, value] of next) {
    if (source.get(key) !== value) return next;
  }
  return source;
}

function stableJson(value: unknown): string {
  if (!value || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(",")}]`;
  }
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
    .join(",")}}`;
}

export function reuseMapIfJsonEntriesUnchanged<K, V>(
  source: Map<K, V>,
  next: Map<K, V>,
): Map<K, V> {
  if (source.size !== next.size) return next;
  for (const [key, value] of next) {
    const current = source.get(key);
    if (current === value) continue;
    if (stableJson(current) !== stableJson(value)) return next;
  }
  return source;
}

function reuseUnchangedExecutionMapRefs(
  source: ExecutionMapSet,
  next: ExecutionMapSet,
): ExecutionMapSet {
  return {
    executions: reuseMapIfEntriesUnchanged(source.executions, next.executions),
    forExecutions: reuseMapIfEntriesUnchanged(
      source.forExecutions,
      next.forExecutions,
    ),
    contentExecutions: reuseMapIfEntriesUnchanged(
      source.contentExecutions,
      next.contentExecutions,
    ),
    importExecutions: reuseMapIfEntriesUnchanged(
      source.importExecutions,
      next.importExecutions,
    ),
    useExecutions: reuseMapIfEntriesUnchanged(
      source.useExecutions,
      next.useExecutions,
    ),
  };
}

export function executionMapRefsChanged(
  source: ExecutionMapSet,
  next: ExecutionMapSet,
): boolean {
  return (
    source.executions !== next.executions ||
    source.forExecutions !== next.forExecutions ||
    source.contentExecutions !== next.contentExecutions ||
    source.importExecutions !== next.importExecutions ||
    source.useExecutions !== next.useExecutions
  );
}

export function createHydratedExecutionMaps(
  source: ExecutionMapSet,
  outputs: Record<string, ExecutionUnitOutput> | undefined,
  options: ApplyHydratedUnitOptions = {},
): ExecutionMapSet {
  if (!outputs) return source;
  const maps = cloneExecutionMaps(source);

  for (const [unitId, rawOutput] of Object.entries(outputs)) {
    if (!rawOutput) continue;
    applyHydratedUnitOutputToExecutionMaps(maps, unitId, rawOutput, options);
  }

  return reuseUnchangedExecutionMapRefs(source, maps);
}

export function pruneStaleImportedNamespaceExecutions(
  source: ExecutionMapSet,
  namespace: string,
  outputs: Record<string, ExecutionUnitOutput> | undefined,
): ExecutionMapSet {
  const prefix = `${namespace}/`;
  const availableOutputIds = new Set(
    Object.keys(outputs ?? {}).filter((unitId) => unitId.startsWith(prefix)),
  );
  const maps = cloneExecutionMaps(source);
  let changed = false;
  const prune = <T>(map: Map<string, T>) => {
    for (const unitId of map.keys()) {
      if (!unitId.startsWith(prefix)) continue;
      if (availableOutputIds.has(unitId)) continue;
      map.delete(unitId);
      changed = true;
    }
  };

  prune(maps.executions);
  prune(maps.forExecutions);
  prune(maps.contentExecutions);

  return changed ? reuseUnchangedExecutionMapRefs(source, maps) : source;
}

export function clearChangedUnitExecutions(
  source: ExecutionMapSet,
  unitIds: Iterable<string> | undefined,
): ExecutionMapSet {
  const maps = cloneExecutionMaps(source);
  if (!unitIds) return maps;

  for (const unitId of unitIds) {
    const existing = maps.executions.get(unitId);
    const keepRunning = existing?.status === "running";
    maps.executions.set(unitId, {
      ...(existing ?? emptyExecutionEntry),
      unitId,
      status: keepRunning ? "running" : "idle",
      result: null,
      output: null,
      duration: keepRunning ? null : (existing?.duration ?? null),
    });
    maps.forExecutions.delete(unitId);
    maps.contentExecutions.delete(unitId);
    maps.importExecutions.delete(unitId);
    maps.useExecutions.delete(unitId);
  }

  return maps;
}

export function withoutChangedOutputs(
  outputs: Record<string, ExecutionUnitOutput> | undefined,
  changedUnitIds: Set<string>,
): Record<string, ExecutionUnitOutput> | undefined {
  if (!outputs || changedUnitIds.size === 0) return outputs;

  const next: Record<string, ExecutionUnitOutput> = {};
  for (const [unitId, output] of Object.entries(outputs)) {
    if (!changedUnitIds.has(unitId)) next[unitId] = output;
  }
  return next;
}

export function createHydratedUnitOutputPatch(
  source: ExecutionMapSet,
  unitId: string,
  rawOutput: ExecutionUnitOutput,
  options: ApplyHydratedUnitOptions = {},
): ExecutionMapSet {
  const maps = cloneExecutionMaps(source);
  applyHydratedUnitOutputToExecutionMaps(maps, unitId, rawOutput, options);
  return reuseUnchangedExecutionMapRefs(source, maps);
}
